import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import { toast } from 'sonner';

import { preferencesEndpoints as endpoints } from '@/api/endpoints';
import { callGet, callPut } from '@/api/fetcher';
import { selectAccountSlug } from '@/redux/session/selectors';
import { Preference, Preferences, UserPreference, UserPreferences } from '@/shared/types/user';

import { useAccount } from './useAccount';

export const API = {
  getUserPreferences: async (): Promise<UserPreference> => callGet(endpoints.userPrefs),
  getAccountPreferences: async (): Promise<Preference> => callGet(endpoints.accountPrefs),
  getAccountUserPreferences: async (): Promise<Preference> => callGet(endpoints.accountUserPrefs),

  updateUserPreferences: async (preferences: Partial<UserPreferences>): Promise<UserPreference> =>
    callPut(endpoints.userPrefs, preferences),
  updateAccountPreferences: async (preferences: Preferences): Promise<Preference> =>
    callPut(endpoints.accountPrefs, preferences),
  updateAccountUserPreferences: async (preferences: Preferences): Promise<Preference> =>
    callPut(endpoints.accountUserPrefs, preferences),
};

export const usePreferences = () => {
  const queryClient = useQueryClient();
  const { t } = useTranslation();
  const slug = useSelector(selectAccountSlug);
  const { accounts } = useAccount();
  const queryConfig = { enabled: !!slug };

  const { data: userPreferences } = useQuery<UserPreference>({
    queryKey: [endpoints.userPrefs],
    queryFn: API.getUserPreferences,
    enabled: !!slug || accounts?.length === 0,
  });

  const { data: accountPreferences, isLoading } = useQuery<Preference>({
    queryKey: [endpoints.accountPrefs, slug],
    queryFn: () => API.getAccountPreferences(),
    ...queryConfig,
  });

  const { data: accountUserPrefs, status: accountUserPrefStatus } = useQuery<Preference>({
    queryKey: [endpoints.accountUserPrefs, slug],
    queryFn: API.getAccountUserPreferences,
    ...queryConfig,
  });

  const {
    mutate: updateUserPreferences,
    mutateAsync: updateUserPreferencesAsync,
    status: updateUserPreferencesStatus,
  } = useMutation<UserPreference, Error, Partial<UserPreferences>>({
    mutationFn: API.updateUserPreferences,
    onSuccess: (resp) => {
      queryClient.setQueryData<UserPreference>([endpoints.userPrefs], (prev) => ({
        ...prev,
        ...resp,
      }));
    },
    onError: () => {
      toast.error(t('common.could_not_save_changes'));
    },
  });

  const {
    mutate: updateAccountPreferences,
    mutateAsync: updateAccountPreferencesAsync,
    status: updateAccountPreferencesStatus,
  } = useMutation<Preference, Error, Preferences>({
    mutationFn: API.updateAccountPreferences,
    onSuccess: (resp) => {
      toast.success(t('accounts.updated'));
      queryClient.setQueryData<Preference>([endpoints.accountPrefs, slug], (prev) => ({
        ...prev,
        ...resp,
      }));
    },
    meta: { errorToast: true },
  });

  const {
    mutate: updateAccountUserPreferences,
    mutateAsync: updateAccountUserPreferencesAsync,
    status: updateAccountUserPreferencesStatus,
  } = useMutation<Preference, Error, Preferences>({
    mutationFn: API.updateAccountUserPreferences,
    onSuccess: (resp) => {
      queryClient.setQueryData<Preference>([endpoints.accountUserPrefs, slug], (prev) => ({
        ...prev,
        ...resp,
      }));
    },
    onError: () => {
      toast.error(t('common.could_not_save_changes'));
    },
  });

  return {
    userPreferences: userPreferences?.preferences,
    accountPreferences: accountPreferences?.preferences,
    accountUserPrefs: accountUserPrefs?.preferences,
    isLoadingAccountPreferences: isLoading,
    updateUserPreferences,
    updateUserPreferencesAsync,
    updateAccountPreferences,
    updateAccountPreferencesAsync,
    updateAccountUserPreferences,
    updateAccountUserPreferencesAsync,
    accountUserPrefStatus,
    updateUserPreferencesStatus,
    updateAccountPreferencesStatus,
    updateAccountUserPreferencesStatus,
  };
};
